import { PlayIcon } from "./product-icons.tsx";

import { VideoWithFallback } from "./video-with-fallback.tsx";
import { isVideoMedia } from "@wags/utils/media.ts";
import type React from "react";
import { cn } from "../../lib/utils.ts";
import { ImageErrorFallback, VercelImage } from "./vercel-image.tsx";

const interactiveMediaClass =
    "transition-[filter] duration-150 [@media(hover:hover)]:hover:brightness-97";

type MediaThumbnailPlaceholder = {
    src?: string;
    width: number;
    height: number;
    className?: string;
};

type MediaThumbnailProps = {
    media: { url: string; mimeType?: string | null };
    alt: string;
    width: number;
    height: number;
    loading?: React.ImgHTMLAttributes<HTMLImageElement>["loading"];
    priority?: boolean;
    listingMedia?: boolean;
    placeholder?: MediaThumbnailPlaceholder;
    loadingPlaceholder?: React.ReactNode;
    className?: string;
    mediaClassName?: string;
    variant?: "default" | "interactive";
    playIconContainerClassName?: string;
    playIconClassName?: string;
};

export function MediaThumbnail({
    media,
    alt,
    width,
    height,
    loading,
    priority = false,
    listingMedia = false,
    placeholder,
    loadingPlaceholder,
    className,
    mediaClassName,
    variant = "default",
    playIconContainerClassName,
    playIconClassName,
}: MediaThumbnailProps) {
    const isVideo = isVideoMedia(media);
    const mediaClasses = cn(
        "h-full w-full object-cover",
        variant === "interactive" && interactiveMediaClass,
        mediaClassName,
    );

    const thumbnailSrc = isVideo ? `${media.url}#t=0.001` : media.url;

    return (
        <div className={cn("relative isolate", className)}>
            {!isVideo && placeholder ? (
                <VercelImage
                    src={placeholder.src ?? media.url}
                    listingMedia={listingMedia}
                    alt=""
                    aria-hidden="true"
                    width={placeholder.width}
                    height={placeholder.height}
                    loading={priority ? "eager" : "lazy"}
                    fetchPriority={priority ? "low" : "auto"}
                    className={cn(
                        "absolute inset-0 h-full w-full scale-110 object-cover blur-2xl brightness-95 saturate-110",
                        placeholder.className,
                    )}
                />
            ) : null}
            {isVideo ? (
                <VideoWithFallback
                    fallback={
                        <ImageErrorFallback
                            alt={alt}
                            className={mediaClasses}
                        />
                    }
                    overlay={
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                            <div
                                className={cn(
                                    "flex size-14 items-center justify-center rounded-full bg-true-black/55 backdrop-blur-[10px] text-true-white shadow-[0_12px_32px_rgba(0,0,0,0.24)]",
                                    playIconContainerClassName,
                                )}
                            >
                                <PlayIcon
                                    aria-hidden="true"
                                    className={cn(
                                        "size-5 fill-current",
                                        playIconClassName,
                                    )}
                                />
                            </div>
                        </div>
                    }
                    src={thumbnailSrc}
                    aria-label={alt}
                    muted
                    playsInline
                    preload="metadata"
                    className={mediaClasses}
                />
            ) : (
                <VercelImage
                    src={media.url}
                    mimeType={media.mimeType}
                    alt={alt}
                    width={width}
                    height={height}
                    loading={loading}
                    priority={priority}
                    listingMedia={listingMedia}
                    loadingPlaceholder={loadingPlaceholder}
                    className={cn("relative z-10", mediaClasses)}
                />
            )}
        </div>
    );
}

export function MediaThumbnailButton({
    media,
    label,
    selected,
    onClick,
    size,
    listingMedia,
    ref,
}: {
    media: MediaThumbnailProps["media"];
    label: string;
    selected: boolean;
    onClick: () => void;
    size: "xs" | "sm" | "md";
    listingMedia?: boolean;
    ref?: React.Ref<HTMLButtonElement>;
}) {
    return (
        <button
            ref={ref}
            type="button"
            aria-label={label}
            aria-current={selected}
            onClick={onClick}
            className={cn(
                "group relative shrink-0 cursor-pointer overflow-hidden transition-transform duration-200 ease-out-quint motion-reduce:transition-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-true-white rounded-nested",
                size !== "xs" && "aria-[current=true]:scale-105",
                size === "xs"
                    ? "size-11 md:size-14"
                    : size === "sm"
                      ? "size-14"
                      : "size-16",
            )}
        >
            <MediaThumbnail
                media={media}
                alt=""
                listingMedia={listingMedia}
                variant="interactive"
                width={128}
                height={128}
                className="size-full overflow-hidden rounded-[inherit]"
                mediaClassName="object-cover"
                playIconContainerClassName={size === "xs" ? "size-5" : "size-7"}
                playIconClassName={size === "xs" ? "size-2.5" : "size-3.5"}
            />
            <span
                aria-hidden="true"
                data-slot="media-selection"
                className={cn(
                    "pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-200 ease-out-quint motion-reduce:transition-none group-aria-[current=true]:opacity-100",
                    size === "xs"
                        ? "border border-true-white"
                        : "border-2 border-labrador-yellow",
                )}
            />
        </button>
    );
}
